// frontend/src/pages/Billing.jsx
// Single-file Billing page (no extra API files).
// - Reads tenantId from prop OR from ?tenantId= on URL (optional)
// - Calls backend routes:
//     GET  /billing/wallet
//     GET  /billing/config
//     GET  /billing/estimate
//     POST /billing/add-credit   { amountUSD, note? }
//     GET  /billing/charges?page=1&pageSize=20
//
// Notes:
// - Uses relative paths (same origin). If you have a proxy/base, set REACT_APP_API_BASE.
// - UI text is in English per your requirement.

import React from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '';

function buildUrl(path, params = {}) {
  const u = new URL(`${API_BASE}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, v);
  });
  return u.toString();
}

async function apiGet(path, params) {
  const res = await fetch(buildUrl(path, params), { credentials: 'include' });
  if (!res.ok) throw new Error((await res.text()) || 'Request failed');
  return res.json();
}

async function apiPost(path, body, params) {
  const res = await fetch(buildUrl(path, params), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Request failed';
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

function useResolvedTenantId(propTenantId) {
  const [tid] = React.useState(() => {
    if (propTenantId) return String(propTenantId);
    const sp = new URLSearchParams(window.location.search);
    const q = sp.get('tenantId');
    return q ? String(q) : null;
  });
  return tid;
}

function fmt(n, digits = 6) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toFixed(digits);
}

export default function Billing({ tenantId: propTenantId }) {
  const tenantId = useResolvedTenantId(propTenantId);

  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState('');

  const [wallet, setWallet] = React.useState(null);
  const [estimate, setEstimate] = React.useState(null);
  const [config, setConfig] = React.useState(null);

  const [charges, setCharges] = React.useState({
    items: [],
    total: 0,
    page: 1,
    pageSize: 20,
  });

  const refresh = React.useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const [w, c, e, ch] = await Promise.all([
        apiGet('/billing/wallet', tenantId ? { tenantId } : undefined),
        apiGet('/billing/config'),
        apiGet('/billing/estimate'),
        apiGet('/billing/charges', { ...(tenantId ? { tenantId } : {}), page, pageSize: 20 }),
      ]);
      setWallet(w);
      setConfig(c);
      setEstimate(e);
      setCharges(ch);
    } catch (err) {
      setError(err.message || 'Failed to load billing data.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => { refresh(1); }, [refresh]);

  async function handleAddCredit(amount = 10) {
    try {
      setAdding(true);
      setError('');
      await apiPost('/billing/add-credit', { amountUSD: amount, note: `Top up ${amount.toFixed(2)}` }, tenantId ? { tenantId } : undefined);
      await refresh(charges.page);
    } catch (err) {
      setError(err.message || 'Failed to add credit.');
    } finally {
      setAdding(false);
    }
  }

  const lowBalance = !!wallet?.lowBalance;
  const sendingBlocked = lowBalance && wallet?.blockSending;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Billing</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refresh(charges.page)}
            className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200"
            disabled={loading}
            title="Refresh"
          >
            Refresh
          </button>
          <button
            onClick={() => handleAddCredit(10)}
            className="px-4 py-2 rounded-xl bg-black text-white hover:opacity-90 disabled:opacity-50"
            disabled={adding}
            title="Add $10 credit"
          >
            {adding ? 'Adding…' : 'Add $10'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      {/* Low balance banner */}
      {wallet && (
        <div className={`mb-6 p-5 rounded-2xl border shadow-sm ${lowBalance ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm text-gray-500">Current Balance</div>
              <div className="text-3xl font-bold">${fmt(wallet.balanceUSD, 4)}</div>
              <div className="text-sm text-gray-500 mt-1">
                Minimum required: ${fmt(wallet.minBalanceUSD, 2)}{' '}
                {lowBalance && <span className="ml-2 text-amber-700 font-medium">(Low balance)</span>}
              </div>
              {tenantId && (
                <div className="text-xs text-gray-400 mt-1">Tenant: {tenantId}</div>
              )}
            </div>

            <div className="text-right">
              <div className="text-sm text-gray-500">Sending allowed</div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm mt-1 ${sendingBlocked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {sendingBlocked ? 'No (wallet below minimum)' : 'Yes'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing / Estimate */}
      {estimate && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="text-sm text-gray-500 mb-2">Base Provider Prices</div>
            <ul className="text-sm leading-7">
              <li>SMS: ${fmt(estimate.base.smsUSD)}</li>
              <li>WhatsApp: ${fmt(estimate.base.waUSD)}</li>
              <li>Token (input): ${fmt(estimate.base.tokenInputUSD, 8)}</li>
              <li>Token (output): ${fmt(estimate.base.tokenOutputUSD, 8)}</li>
            </ul>
          </div>
          <div className="p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="text-sm text-gray-500 mb-2">Billed Prices (with markup ×{estimate.markupFactor})</div>
            <ul className="text-sm leading-7">
              <li>SMS: ${fmt(estimate.billed.smsUSD)}</li>
              <li>WhatsApp: ${fmt(estimate.billed.waUSD)}</li>
              <li>Token (input): ${fmt(estimate.billed.tokenInputUSD, 8)}</li>
              <li>Token (output): ${fmt(estimate.billed.tokenOutputUSD, 8)}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Charges Table */}
      <div className="p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Charges</h2>
          <div className="text-sm text-gray-500">Total: {charges?.total || 0}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Source</th>
                <th className="py-2 pr-3">Direction</th>
                <th className="py-2 pr-3">Amount (USD)</th>
                <th className="py-2 pr-3">Provider Cost</th>
                <th className="py-2 pr-3">Markup</th>
              </tr>
            </thead>
            <tbody>
              {(charges?.items || []).map((c) => (
                <tr key={c._id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{new Date(c.createdAt).toLocaleString()}</td>
                  <td className="py-2 pr-3">{c.source}</td>
                  <td className="py-2 pr-3">{c.direction}</td>
                  <td className="py-2 pr-3 font-medium">${fmt(c.amountUSD)}</td>
                  <td className="py-2 pr-3">${fmt(c.providerCostUSD)}</td>
                  <td className="py-2 pr-3">×{Number(c.markupFactor || 1)}</td>
                </tr>
              ))}
              {(!charges?.items || charges.items.length === 0) && (
                <tr>
                  <td className="py-4 text-gray-500" colSpan={6}>No charges yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <button
            className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            disabled={loading || (charges.page <= 1)}
            onClick={() => refresh(charges.page - 1)}
          >
            Previous
          </button>
          <div className="text-sm text-gray-500">Page {charges.page}</div>
          <button
            className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            disabled={loading || ((charges.page * charges.pageSize) >= (charges.total || 0))}
            onClick={() => refresh(charges.page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
